// Task #3976 — Admin "Coming Soon" feature gating: client resolver.
//
// ONE hook + a handful of PURE helpers shared by every client consumer (the
// app-layout nav + route overlay and the Financials tab gating). The hook reads
// the per-user resolver endpoint with maximum freshness so an admin flipping a
// gate is reflected on the next mount / window refocus / query invalidation —
// it deliberately OVERRIDES the global queryClient cache defaults.
//
// The server middleware enforces the SAME decision on the API, so these helpers
// only drive UX (hide nav, swap a route/tab for an overlay). They never need to
// "fail closed": if the gate state has not loaded yet the page renders and any
// gated data request is still blocked server-side.

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  matchSectionRouteTargets,
  targetsForNavHref,
  getGateableTarget,
  pathMatchesPrefix,
  targetMatchesRoute,
  type GateMode,
  type ResolvedGate,
} from "@shared/feature-gates";

export type GateBlock = { mode: GateMode; message: string | null; key: string };

export type FeatureGatesState = {
  gates: ResolvedGate[];
  /** Resolved gates keyed by target key (only ENABLED gates are present). */
  byKey: Map<string, ResolvedGate>;
  /** false only on the very first cold load (no cached data yet). */
  loaded: boolean;
  /** A failed lookup must not expose gated Home cards from cached decisions. */
  failed?: boolean;
};

export function useFeatureGates(): FeatureGatesState {
  const query = useQuery<ResolvedGate[]>({
    queryKey: ["/api/app/feature-gates"],
    // Always re-resolve on mount + refocus; never serve a stale gate decision.
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });
  // Defensive: the resolver always returns an array, but a not-yet-resolved or
  // malformed response (and AppLayout-rendering tests that don't mock this
  // endpoint) can hand back a non-array — never let that crash the whole shell.
  const gates = Array.isArray(query.data) ? query.data : [];
  const byKey = useMemo(
    () => new Map(gates.map((g) => [g.key, g] as const)),
    [gates],
  );
  return { gates, byKey, loaded: !query.isPending, failed: query.isError };
}

/** A gate only "applies" to this user when it is present (enabled) AND gated. */
function appliedGate(
  byKey: Map<string, ResolvedGate>,
  key: string,
): ResolvedGate | undefined {
  const g = byKey.get(key);
  return g && g.gated ? g : undefined;
}

/**
 * Route-level decision for `location`. Considers ONLY top-level section targets
 * (sub-tab targets carry a `group` and are owned by their page). The MOST
 * specific (longest matched route prefix) matching target decides this path: if
 * the user is EXEMPT from it, it SHADOWS any broader ancestor section gate for
 * its own paths (so a more-granular grant wins), otherwise it blocks with its
 * own message. A standalone target that nests under a broader section therefore
 * stays fully independent: e.g. a Collections beta grant unlocks
 * `/app/financials/collections` even when the Revenue section
 * (`/app/financials`) is also gated.
 */
export function gateBlockForRoute(
  byKey: Map<string, ResolvedGate>,
  location: string,
): GateBlock | null {
  // Task #4017 — path-scoped sub-tab→section exemption. A granted sub-tab whose
  // route covers `location` exempts THIS path from its parent section gate, the
  // same rule the server enforces in resolveGateDecision. The exemption is
  // path-scoped: a Cards grant only covers Cards paths, so the rest of the
  // section stays gated. (Sub-tab grants are surfaced by the resolver as
  // `exempt` entries even when the sub-tab itself isn't gated.)
  const sectionExemptViaSubTab = new Set<string>();
  for (const g of byKey.values()) {
    if (!g.group || !g.exempt) continue;
    const target = getGateableTarget(g.key);
    if (target && targetMatchesRoute(target, location)) {
      sectionExemptViaSubTab.add(g.group);
    }
  }
  let best: GateBlock | null = null;
  let bestLen = -1;
  for (const m of matchSectionRouteTargets(location)) {
    if (sectionExemptViaSubTab.has(m.target.key)) continue;
    // Consider any ENABLED gate entry (gated OR exempt). The most specific
    // matching target decides: an EXEMPT more-specific target shadows broader
    // ancestor gates for its own paths (e.g. a Collections grant clears the
    // Revenue block on `/app/financials/collections`); a gated one blocks.
    const g = byKey.get(m.target.key);
    if (!g) continue;
    if (m.prefix.length > bestLen) {
      bestLen = m.prefix.length;
      best = g.gated ? { mode: g.mode, message: g.message, key: g.key } : null;
    }
  }
  return best;
}

/** Key-level decision — used by pages that gate their own sub-tabs by key. */
export function gateBlockForKey(
  byKey: Map<string, ResolvedGate>,
  key: string,
): GateBlock | null {
  const g = appliedGate(byKey, key);
  return g ? { mode: g.mode, message: g.message, key } : null;
}

/**
 * Whether a sidebar href should be HIDDEN from nav. Only the `hidden` mode
 * removes nav — `coming_soon` keeps the link visible (clicking it lands on the
 * Coming Soon overlay). A href can be controlled by more than one target (e.g.
 * the P&L link is covered by both the Revenue section and the P&L sub-tab).
 */
export function navHiddenFor(
  byKey: Map<string, ResolvedGate>,
  href: string,
): boolean {
  const targets = targetsForNavHref(href);
  // Task #4017 — path-scoped sub-tab→section exemption for nav visibility: a
  // granted sub-tab controlling THIS href exempts the href from its parent
  // section's hidden gate (e.g. a granted Income tab keeps its sidebar link
  // visible even while the rest of the Revenue section is hidden). Scoped to the
  // href's own targets, so other section links stay hidden.
  const sectionExemptViaSubTab = new Set<string>();
  for (const t of targets) {
    if (!t.group) continue;
    const g = byKey.get(t.key);
    if (g?.exempt) sectionExemptViaSubTab.add(t.group);
  }
  for (const t of targets) {
    if (sectionExemptViaSubTab.has(t.key)) continue;
    const g = appliedGate(byKey, t.key);
    if (g && g.mode === "hidden") return true;
  }
  return false;
}

/**
 * Whether a whole nav SECTION (by target key) is hidden. Used to drop group
 * headers (Devices, Vendors) that would otherwise render empty once all their
 * items are hidden. `coming_soon` keeps the header so the section stays
 * discoverable.
 */
export function isSectionNavHidden(
  byKey: Map<string, ResolvedGate>,
  key: string,
): boolean {
  const g = appliedGate(byKey, key);
  return !!g && g.mode === "hidden";
}

/**
 * "Coming soon" badge text for a sidebar href, or null. Returns the admin
 * `gateMessage` whenever a target controlling this href has an ENABLED gate in
 * `coming_soon` mode — surfaced for gated AND exempt (beta) users alike so the
 * nav advertises the pending public launch even to a tester who can already
 * open the page. `hidden`-mode gates never badge (the link is removed instead,
 * see `navHiddenFor`). When more than one target covers the href, the most
 * specific (longest matching route prefix) wins, mirroring `gateBlockForRoute`
 * so the badge text matches the overlay the link leads to.
 */
export function comingSoonBadgeForNav(
  byKey: Map<string, ResolvedGate>,
  href: string,
): string | null {
  let bestLen = -1;
  let bestMessage: string | null = null;
  for (const t of targetsForNavHref(href)) {
    const g = byKey.get(t.key);
    if (!g || g.mode !== "coming_soon") continue;
    const len = t.routePrefixes.reduce(
      (max, p) => (pathMatchesPrefix(href, p) && p.length > max ? p.length : max),
      t.routeExactPaths?.includes(href) ? href.length : 0,
    );
    if (len > bestLen) {
      bestLen = len;
      bestMessage = g.message;
    }
  }
  return bestLen >= 0 ? bestMessage : null;
}
